"use client";

/**
 * Folder create/edit dialog.
 */

import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogHeader, DialogBody } from "@speakai/ui/dialog";
import { useToast } from "@speakai/ui/toast";
import { useTranslation } from "react-i18next";
import {
  useCreateFolder,
  useUpdateFolder,
  useSetupFolderAutomation,
} from "../mutations";
import { useAssignToList } from "@/features/team/queries";
import { usePermissions } from "@/hooks/use-permissions";
import { usePlatform } from "@/hooks/use-platform";
import { usePersonalizationContext } from "@/hooks/use-personalization-context";
import { generateFields } from "@/features/fields/api";
import { useFields } from "@/features/fields/queries";
import type { Field, GeneratedField } from "@/features/fields/api";
import type { Folder } from "../queries";
import { Check, FileText, Settings, Sparkles } from "lucide-react";
import { FolderDialogStepDetails } from "./folder-dialog-step-details";
import { FolderDialogStepFields } from "./folder-dialog-step-fields";
import { FolderDialogStepAutomation } from "./folder-dialog-step-automation";

// -- Validation Constants -------------------------------------------

const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 5000;
const AI_PROMPT_MIN = 10;
const AI_PROMPT_MAX = 500;
const MAX_AUTOMATION_FIELDS = 10;

// -- Source Types & Suggested Fields --------------------------------

const SUB_CATEGORIES: Record<string, { value: string; label: string }[]> = {
  interview: [
    { value: "structured", label: "Structured" },
    { value: "semi-structured", label: "Semi-Structured" },
    { value: "unstructured", label: "Unstructured" },
    { value: "behavioral", label: "Behavioral" },
  ],
  focusGroup: [
    { value: "moderated", label: "Moderated" },
    { value: "unmoderated", label: "Unmoderated" },
    { value: "online", label: "Online" },
  ],
  survey: [
    { value: "quantitative", label: "Quantitative" },
    { value: "qualitative", label: "Qualitative" },
    { value: "mixed", label: "Mixed Methods" },
  ],
  userStudy: [
    { value: "usability", label: "Usability Test" },
    { value: "diary", label: "Diary Study" },
    { value: "ethnographic", label: "Ethnographic" },
  ],
  meeting: [
    { value: "standup", label: "Standup" },
    { value: "retrospective", label: "Retrospective" },
    { value: "planning", label: "Planning" },
    { value: "general", label: "General" },
  ],
  podcast: [
    { value: "solo", label: "Solo" },
    { value: "interview", label: "Interview" },
    { value: "panel", label: "Panel" },
  ],
  webinar: [
    { value: "live", label: "Live" },
    { value: "recorded", label: "Recorded" },
    { value: "interactive", label: "Interactive" },
  ],
  lecture: [
    { value: "academic", label: "Academic" },
    { value: "workshop", label: "Workshop" },
    { value: "seminar", label: "Seminar" },
  ],
  content: [
    { value: "blog", label: "Blog" },
    { value: "video", label: "Video" },
    { value: "social", label: "Social Media" },
  ],
  phoneCall: [
    { value: "sales", label: "Sales" },
    { value: "support", label: "Support" },
    { value: "general", label: "General" },
  ],
  surveyData: [
    { value: "nps", label: "NPS" },
    { value: "csat", label: "CSAT" },
    { value: "general", label: "General" },
  ],
  other: [{ value: "general", label: "General" }],
};

const SOURCE_TYPE_FIELDS: Record<string, string[]> = {
  interview: ["Interviewer", "Interviewee", "Topic", "Date", "Duration"],
  focusGroup: ["Moderator", "Group Size", "Topic", "Date"],
  survey: ["Respondent", "Survey Name", "Completion Status"],
  userStudy: ["Facilitator", "Participant", "Task", "Date"],
  meeting: ["Organizer", "Attendees", "Agenda", "Action Items"],
  podcast: ["Host", "Guest", "Episode Number", "Topic"],
  webinar: ["Presenter", "Topic", "Attendee Count"],
  lecture: ["Instructor", "Course", "Topic", "Date"],
  content: ["Author", "Platform", "Topic", "Date"],
  phoneCall: ["Caller", "Recipient", "Purpose", "Date", "Duration"],
  surveyData: ["Respondent", "Survey Name", "Score", "Date"],
  other: [],
};

const GOAL_TO_SOURCE_TYPE: Partial<Record<string, string>> = {
  Sales: "phoneCall",
  Research: "interview",
  Meetings: "meeting",
  "Interview or Focus Group Transcription & Analysis": "interview",
  "Phone Call Transcription & Analysis": "phoneCall",
  "Meeting Transcription & Summarization": "meeting",
  "Form or Survey Response Analysis": "surveyData",
};

// -- Steps ----------------------------------------------------------

const STEPS = [
  { key: "details", label: "Folder Details", icon: FileText },
  { key: "fields", label: "Generate Fields", icon: Settings },
  { key: "automation", label: "Setup Automation", icon: Sparkles },
] as const;

type StepKey = (typeof STEPS)[number]["key"];

// -- Types -----------------------------------------------------------

interface FolderDialogProps {
  open: boolean;
  onClose: () => void;
  /** When set, dialog is in edit mode */
  folder?: Folder | null;
  /** Callback after successful create/update */
  onSuccess?: (folderId?: string) => void;
  /**
   * Skip the fields/automation wizard steps and finish right after the
   * folder is created — reuses the same short-circuit the mobile branch
   * already takes. For inline "create from a dropdown" flows.
   */
  quickCreate?: boolean;
}

// -- Hooks ----------------------------------------------------------

function useFieldsList() {
  const query = useFields();
  const filtered = useMemo<Field[]>(
    () => (query.data ?? []).filter((f) => f.isActive && !f.isDeleted),
    [query.data]
  );
  return { ...query, data: filtered };
}

// -- Component -------------------------------------------------------

export function FolderDialog({
  open,
  onClose,
  folder,
  onSuccess,
  quickCreate = false,
}: FolderDialogProps) {
  const { t } = useTranslation();
  const isEdit = !!folder;
  const toast = useToast();
  const { isMobile } = usePlatform();
  const { isCompanyUser, canAssignFolder } = usePermissions();
  const showAssignTo = isCompanyUser && canAssignFolder;
  const { onboardingGoal } = usePersonalizationContext();

  // Step state (only relevant in create mode)
  const [currentStep, setCurrentStep] = useState<StepKey>("details");

  // Step 1: Details
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assignTo, setAssignTo] = useState<string[]>([]);

  // Created folder ID (from step 1 → used in steps 2-3)
  const [createdFolderId, setCreatedFolderId] = useState<string | null>(null);

  // Step 2: Source & Fields
  const [sourceType, setSourceType] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(
    new Set()
  );

  // Step 2: AI field generator
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGeneratedFields, setAiGeneratedFields] = useState<GeneratedField[]>([]);
  const [editingAiFieldIdx, setEditingAiFieldIdx] = useState<number | null>(null);
  const [editingAiFieldValue, setEditingAiFieldValue] = useState("");

  // Step 3: Automation
  const [automationFieldIds, setAutomationFieldIds] = useState<Set<string>>(
    new Set()
  );
  const [automationPending, setAutomationPending] = useState(false);

  const createFolder = useCreateFolder();
  const updateFolder = useUpdateFolder();
  const setupAutomation = useSetupFolderAutomation();
  const { data: allFields = [], isLoading: fieldsLoading } = useFieldsList();
  const { data: assignToList = [] } = useAssignToList({ enabled: showAssignTo && open });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(folder?.name ?? "");
      setDescription(folder?.description ?? "");
      setAssignTo(folder?.assignTo ?? []);
      setCurrentStep("details");
      setCreatedFolderId(null);
      setSourceType(!isEdit && onboardingGoal ? (GOAL_TO_SOURCE_TYPE[onboardingGoal] ?? "") : "");
      setSubCategory("");
      setSelectedFieldIds(new Set());
      setAiPrompt("");
      setAiGenerating(false);
      setAiGeneratedFields([]);
      setEditingAiFieldIdx(null);
      setEditingAiFieldValue("");
      setAutomationFieldIds(new Set());
      setAutomationPending(false);
    }
  }, [open, folder, isEdit, onboardingGoal]);

  // Suggested field names for the selected source type
  const suggestedFieldNames = SOURCE_TYPE_FIELDS[sourceType] ?? [];

  // Match suggested field names to actual field objects from API
  const suggestedFields = allFields.filter((f) =>
    suggestedFieldNames.some(
      (suggested) => f.name.toLowerCase() === suggested.toLowerCase()
    )
  );

  // Fields that don't match suggestions (for "Other fields" section)
  const otherFields = allFields.filter(
    (f) =>
      !suggestedFieldNames.some(
        (suggested) => f.name.toLowerCase() === suggested.toLowerCase()
      )
  );

  // Selected field objects (for step 3)
  const selectedFields = allFields.filter((f) => selectedFieldIds.has(f.id));

  // -- Validation ----------------------------------------------------

  const nameError = (() => {
    const trimmed = name.trim();
    if (!trimmed) return t("FOLDERS.VALIDATION.NAME_REQUIRED");
    if (trimmed.length < NAME_MIN_LENGTH)
      return t("FOLDERS.VALIDATION.NAME_TOO_SHORT", { min: NAME_MIN_LENGTH });
    if (trimmed.length > NAME_MAX_LENGTH)
      return t("FOLDERS.VALIDATION.NAME_TOO_LONG", { max: NAME_MAX_LENGTH });
    return null;
  })();

  const descriptionError =
    description.length > DESCRIPTION_MAX_LENGTH
      ? t("FOLDERS.VALIDATION.DESC_TOO_LONG", { max: DESCRIPTION_MAX_LENGTH })
      : null;

  const isFormValid = !nameError && !descriptionError;

  const aiPromptError = (() => {
    const len = aiPrompt.trim().length;
    if (len > 0 && len < AI_PROMPT_MIN) return `At least ${AI_PROMPT_MIN} characters required`;
    if (len > AI_PROMPT_MAX) return `Maximum ${AI_PROMPT_MAX} characters`;
    return null;
  })();

  // -- Handlers ------------------------------------------------------

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    try {
      if (isEdit && folder?.folderId) {
        await updateFolder.mutateAsync({
          folderId: folder.folderId,
          name: name.trim(),
          description: description.trim(),
          // The server overwrites assignTo on every update (an undefined value
          // resets it to []), so in edit mode we always send it or a save would
          // silently wipe the folder's access assignments.
          ...(showAssignTo ? { assignTo } : {}),
        });
        onSuccess?.();
        onClose();
      } else {
        const result = await createFolder.mutateAsync({
          name: name.trim(),
          description: description.trim(),
          ...(showAssignTo && assignTo.length > 0 ? { assignTo } : {}),
        });
        const folderId = result.folderId;
        if (folderId && !isMobile && !quickCreate) {
          setCreatedFolderId(folderId);
          setCurrentStep("fields");
        } else {
          toast.success("Folder created. Visit Folder Settings to add fields and automation.");
          onSuccess?.(folderId ?? undefined);
          onClose();
        }
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Something went wrong";
      toast.error(message);
    }
  };

  const handleGenerateFields = async () => {
    const prompt = aiPrompt.trim();
    if (!sourceType || prompt.length < AI_PROMPT_MIN || aiGenerating) return;
    setEditingAiFieldIdx(null);
    setEditingAiFieldValue("");
    setAiGenerating(true);
    setAiGeneratedFields([]);
    try {
      const fields = await generateFields({ sourceType, description: prompt });
      setAiGeneratedFields(fields);
    } catch {
      toast.error("Failed to generate fields. Please try again.");
    } finally {
      setAiGenerating(false);
    }
  };

  const handleStep2Next = async () => {
    if (!createdFolderId) return;

    if (selectedFieldIds.size === 0) {
      finishDialog();
      return;
    }

    // Persist source data + selected fields to the folder before advancing
    const selectedFieldObjects = allFields.filter((f) =>
      selectedFieldIds.has(f.id)
    );
    try {
      await updateFolder.mutateAsync({
        folderId: createdFolderId,
        // Server requires `name` on every folder PUT — pass the freshly-created folder's name.
        name: name.trim(),
        source: {
          type: sourceType,
          subCategory,
          fields: selectedFieldObjects.map((f) => ({
            id: f.id,
            name: f.name,
            type: f.type,
          })),
        },
      });
    } catch {
      toast.error("Could not save field selection. You can still set up automation.");
    } finally {
      setAutomationFieldIds(new Set([...selectedFieldIds].slice(0, MAX_AUTOMATION_FIELDS)));
      setCurrentStep("automation");
    }
  };

  const handleStep2Skip = () => {
    setAutomationFieldIds(new Set([...selectedFieldIds].slice(0, MAX_AUTOMATION_FIELDS)));
    setCurrentStep("automation");
  };

  const handleSetupAutomation = async () => {
    if (!createdFolderId || automationFieldIds.size === 0) {
      finishDialog();
      return;
    }

    setAutomationPending(true);
    try {
      // NOTE: server endpoint does NOT accept promptText — Joi rejects extra keys with 400.
      // The aiPrompt collected in step 2 is used client-side for field generation only.
      await setupAutomation.mutateAsync({
        folderId: createdFolderId,
        fields: Array.from(automationFieldIds).map((fieldId) => ({ fieldId })),
      });
      toast.success("Field automation configured");
      finishDialog();
    } catch {
      toast.error("Failed to set up automation. You can retry or finish without it.");
    } finally {
      setAutomationPending(false);
    }
  };

  const finishDialog = () => {
    onSuccess?.(createdFolderId ?? undefined);
    onClose();
  };

  const handleToggleAllAutomation = (enabled: boolean) => {
    if (enabled) {
      const ids = [...selectedFieldIds];
      if (ids.length > MAX_AUTOMATION_FIELDS) {
        toast.error(`Only the first ${MAX_AUTOMATION_FIELDS} fields can be automated per folder`);
      }
      setAutomationFieldIds(new Set(ids.slice(0, MAX_AUTOMATION_FIELDS)));
    } else {
      setAutomationFieldIds(new Set());
    }
  };

  const handleToggleAutomationField = (fieldId: string) => {
    setAutomationFieldIds((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
        return next;
      }
      if (next.size >= MAX_AUTOMATION_FIELDS) {
        toast.error(`Maximum ${MAX_AUTOMATION_FIELDS} automated fields per folder`);
        return prev;
      }
      next.add(fieldId);
      return next;
    });
  };

  const isPending = createFolder.isPending || updateFolder.isPending;
  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep);
  const allAutomationEnabled =
    selectedFieldIds.size > 0 &&
    selectedFieldIds.size === automationFieldIds.size &&
    [...selectedFieldIds].every((id) => automationFieldIds.has(id));

  // -- Step Indicator ------------------------------------------------

  const renderStepIndicator = () => {
    if (isEdit || quickCreate) return null;

    return (
      <nav aria-label="Creation steps" className="flex items-center gap-2 mb-4">
        {STEPS.map((step, idx) => {
          const StepIcon = step.icon;
          const isCompleted = idx < currentStepIndex;
          const isCurrent = step.key === currentStep;

          return (
            <div key={step.key} className="flex items-center gap-2">
              {idx > 0 && (
                <div
                  className={`h-px w-8 ${isCompleted ? "bg-primary" : "bg-border"
                    }`}
                />
              )}
              <div
                aria-label={`Step ${idx + 1}: ${step.label}`}
                aria-current={isCurrent ? "step" : undefined}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                  ${isCurrent
                    ? "bg-primary text-primary-foreground"
                    : isCompleted
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground"
                  }
                `}
              >
                {isCompleted ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <StepIcon className="h-3.5 w-3.5" />
                )}
                <span className="truncate">{step.label}</span>
              </div>
            </div>
          );
        })}
      </nav>
    );
  };

  // -- Render --------------------------------------------------------

  return (
    <Dialog open={open} onClose={onClose} size={!isEdit && !quickCreate ? "lg" : "default"}>
      <DialogHeader>
        <h2 className="text-lg font-semibold">
          {isEdit
            ? t("FOLDERS.EDIT_FOLDER")
            : currentStep === "details"
              ? t("FOLDERS.CREATE_FOLDER")
              : currentStep === "fields"
                ? t("FOLDERS.SOURCE_AND_FIELDS")
                : t("FOLDERS.FIELD_AUTOMATION")}
        </h2>
      </DialogHeader>

      <DialogBody>
        {renderStepIndicator()}

        {currentStep === "details" && (
          <FolderDialogStepDetails
            name={name}
            setName={setName}
            nameError={nameError}
            description={description}
            setDescription={setDescription}
            descriptionError={descriptionError}
            assignTo={assignTo}
            setAssignTo={setAssignTo}
            showAssignTo={showAssignTo}
            assignToList={assignToList}
            isFormValid={isFormValid}
            isPending={isPending}
            isEdit={isEdit}
            onSubmit={handleStep1Submit}
            onClose={onClose}
          />
        )}
        {currentStep === "fields" && (
          <FolderDialogStepFields
            sourceType={sourceType}
            setSourceType={setSourceType}
            subCategory={subCategory}
            setSubCategory={setSubCategory}
            subCategoryOptions={SUB_CATEGORIES[sourceType] ?? []}
            selectedFieldIds={selectedFieldIds}
            setSelectedFieldIds={setSelectedFieldIds}
            fieldsLoading={fieldsLoading}
            suggestedFields={suggestedFields}
            otherFields={otherFields}
            aiPrompt={aiPrompt}
            setAiPrompt={setAiPrompt}
            aiPromptError={aiPromptError}
            aiGenerating={aiGenerating}
            aiGeneratedFields={aiGeneratedFields}
            setAiGeneratedFields={setAiGeneratedFields}
            editingAiFieldIdx={editingAiFieldIdx}
            setEditingAiFieldIdx={setEditingAiFieldIdx}
            editingAiFieldValue={editingAiFieldValue}
            setEditingAiFieldValue={setEditingAiFieldValue}
            handleGenerateFields={handleGenerateFields}
            handleStep2Skip={handleStep2Skip}
            handleStep2Next={handleStep2Next}
            updateFolderPending={updateFolder.isPending}
          />
        )}
        {currentStep === "automation" && (
          <FolderDialogStepAutomation
            selectedFieldIds={selectedFieldIds}
            selectedFields={selectedFields}
            automationFieldIds={automationFieldIds}
            allAutomationEnabled={allAutomationEnabled}
            handleToggleAllAutomation={handleToggleAllAutomation}
            handleToggleAutomationField={handleToggleAutomationField}
            handleSetupAutomation={handleSetupAutomation}
            finishDialog={finishDialog}
            automationPending={automationPending}
          />
        )}
      </DialogBody>
    </Dialog>
  );
}
